<script setup lang="ts">
import { RUN_STATUSES } from '@zerohuman/shared'
import { Button, ZhPageTitle, ZhStatusPill, ZhTooltip } from '@zerohuman/ui'
import { computed, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { api, formatAgo, formatDuration, type RunRow, runStoppedLine, skipReason } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import FirstRunHint from '@/components/FirstRunHint.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { useListState } from '@/composables/useListState'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { isFirstRun } from '@/lib/firstRun'
import { runPillStatus, statusTone } from '@/lib/statusTone'

const router = useRouter()
const route = useRoute()
/** `/runs?member=<id>`: only that teammate's runs, where the Activity report's runs link lands (#2123). */
const memberFilter = computed(() => {
    const member = (route as ReturnType<typeof useRoute> | undefined)?.query['member']
    return typeof member === 'string' ? member : ''
})
const runs = ref<RunRow[]>([])
const total = ref(0)
const page = ref(1)
const limit = ref(25)
const pages = ref(1)
const search = ref('')
const sort = ref('createdAt')
const order = ref<'ASC' | 'DESC'>('DESC')
const filters = ref<Record<string, string>>({})
/**
 * #633: the search, sort and filters come back on the next visit, in place before the first request. `?member=` is
 * the Activity link's own filter, with no control here, so it is never saved.
 */
const list = useListState('runs', {
    search: { ref: search },
    sort: { ref: sort },
    order: { ref: order },
    filters: { ref: filters },
})
/**
 * Once this visit has narrowed the list (a saved choice or a new one), the table stays: Clear filters on an empty
 * enterprise shows the cleared, empty table rather than swapping it for the first-run hint under the person's click.
 */
const narrowedThisVisit = ref(false)
watch(
    [search, filters],
    () => {
        if (search.value || Object.values(filters.value).some(Boolean)) {
            narrowedThisVisit.value = true
        }
    },
    { immediate: true, deep: true },
)
const loading = ref(false)
const error = ref<string | null>(null)
const retrying = ref<string | null>(null)
const retryable = new Set(['failed', 'cancelled', 'skipped_overlap', 'blocked'])
/** A run parked on something it cannot finish by itself: the row a human has to look at. */
const waiting = new Set(['waiting_on_gate', 'waiting_on_run'])
/** Ticks every second so a running row's duration and "X Ago" stay live. */
const now = ref(Date.now())
const clock = setInterval(() => {
    now.value = Date.now()
}, 1000)
/** Runs change underneath the page: refresh quietly, in place, so rows animate instead of the table blinking. */
const REFRESH_MS = 2000
const refresh = setInterval(() => {
    if (document.visibilityState !== 'hidden') {
        void load({ silent: true })
    }
}, REFRESH_MS)
/** Runs still `running` in the current search and filters, pinned above page 1 wherever they sort. */
const pinned = ref<RunRow[]>([])
let inFlight = false

onUnmounted(() => {
    clearInterval(clock)
    clearInterval(refresh)
})

/**
 * Running runs first, then the page in its sort order. A run that stops running drops out of the pinned set on the
 * next refresh and glides back to its sort position (or leaves the page when that position is elsewhere).
 */
const rows = computed(() => {
    const live = [...pinned.value, ...runs.value].filter(run => run.status === 'running')
    const pins = live.filter((run, index) => live.findIndex(other => other.id === run.id) === index)
    const pinnedIds = new Set(pins.map(run => run.id))
    return [...pins, ...runs.value.filter(run => !pinnedIds.has(run.id))]
})

/**
 * No run has ever happened, and no search or filter is narrowing the list: the page teaches the loop
 * instead of only saying "No rows.". A search that narrows real runs to zero is not this state.
 */
const firstRun = computed(
    () => !narrowedThisVisit.value && isFirstRun(loading.value, total.value, search.value, filters.value),
)

/** A live run's row is marked by the kit's active-state token, so the screen names the state and not its hue. */
function rowClass(row: Record<string, unknown>): string | undefined {
    return row.status === 'running' ? 'bg-state-active/5' : undefined
}

const columns: DataTableColumn[] = [
    // Filtering is the backend's exact `taskId` match, so the Task filter takes an id; the cell
    // still reads as the task's name.
    { key: 'taskId', label: 'Task', type: 'text' },
    { key: 'assigneeName', label: 'Assignee', type: 'text', filterable: false },
    {
        key: 'status',
        label: 'Status',
        sort: 'status',
        type: 'select',
        options: RUN_STATUSES.map(status => ({ value: status, label: status })),
    },
    { key: 'createdAt', label: 'Started', sort: 'createdAt', type: 'date', filterable: false },
    { key: 'duration', label: 'Duration', type: 'text', filterable: false },
    { key: 'skipReason', label: 'Skip reason', type: 'text', filterable: false },
    { key: 'id', label: 'Run', type: 'text', filterable: false, defaultVisible: false },
]

async function load(options: { silent?: boolean } = {}): Promise<void> {
    if (options.silent && inFlight) {
        return
    }
    inFlight = true
    if (!options.silent) {
        loading.value = true
    }
    try {
        const query = {
            page: page.value,
            limit: limit.value,
            search: search.value,
            sort: sort.value,
            order: order.value,
            ...filters.value,
            ...(memberFilter.value ? { memberId: memberFilter.value } : {}),
        }
        // Pin only on page 1 and when no status filter already decides which runs show.
        const pins = page.value === 1 && !filters.value['status']
        // The page request is made last: it is the one the table (and its filters) are built from.
        const [running, listed] = await Promise.all([
            pins ? api.runs({ ...query, limit: 100, status: 'running' }) : Promise.resolve(null),
            api.runs(query),
        ])
        pinned.value = running?.data ?? []
        runs.value = listed.data
        total.value = listed.total
        page.value = listed.page
        limit.value = listed.limit
        pages.value = listed.pages
        error.value = null
    } catch {
        error.value = 'Could not load runs.'
    } finally {
        loading.value = false
        inFlight = false
    }
}

watch([page, limit, search, sort, order, filters, memberFilter], () => load(), { immediate: true })

/** What the row is for: the task's name, falling back to its slug and then its id. */
function taskLabel(row: Record<string, unknown>): string {
    return String(row.taskName || row.taskSlug || row.taskId || '—')
}

/**
 * When the run started, falling back to when it was created if it has not started yet — the single
 * timestamp `duration`, `startedAgo`, and `startedTitle` all read the row by.
 */
function runStartedAt(row: Record<string, unknown>): string | null {
    const started = row.startedAt ?? row.createdAt
    return typeof started === 'string' ? started : null
}

/** How long the run took, or how long it has been going if it has not finished. */
function duration(row: Record<string, unknown>): string {
    const started = runStartedAt(row)
    if (started === null) {
        return '—'
    }
    const from = new Date(started).getTime()
    const finished = typeof row.finishedAt === 'string' ? new Date(row.finishedAt).getTime() : now.value
    return formatDuration(finished - from)
}

function startedAgo(row: Record<string, unknown>): string {
    const started = runStartedAt(row)
    if (started === null) {
        return '—'
    }
    return formatAgo(now.value - new Date(started).getTime())
}

function startedTitle(row: Record<string, unknown>): string {
    const started = runStartedAt(row)
    return started === null ? '' : new Date(started).toLocaleString()
}

function open(row: Record<string, unknown>): void {
    void router.push(`/runs/${String(row.id)}`)
}

async function retry(id: string): Promise<void> {
    // #1796: a retry starts model work, so without an AI account nothing is sent and the prompt opens.
    if (!(await requireAiAccount())) return
    retrying.value = id
    error.value = null
    try {
        const next = await api.retryRun(id)
        await router.push(`/runs/${next.id}`)
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not retry (${caught.message}).` : 'Could not retry.'
    } finally {
        retrying.value = null
    }
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle>Runs</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			Individual task runs. Follow their linked handoffs in <RouterLink to="/executions" class="text-foreground underline">Executions</RouterLink>.
			Start new work from Tasks.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<FirstRunHint
			v-if="firstRun"
			lead="Nothing has run yet. Runs of your tasks appear here."
			:action="{ label: 'Go to Tasks', to: '/tasks' }"
		/>
		<!--
			No runs at all: the hint above says so, and an empty table under it repeats "No rows." beneath
			search, filter and column controls that can only narrow nothing. A search or filter that narrows
			real runs to zero keeps the table, or there would be no way left to clear it.
		-->
		<DataTable
			v-if="!firstRun"
			storage-key="runs"
			:columns="columns"
			:rows="rows as unknown as Record<string, unknown>[]"
			:row-class="rowClass"
			animate-rows
			:total="total"
			:page="page"
			:limit="limit"
			:pages="pages"
			:loading="loading"
			:search="search"
			:sort="sort"
			:order="order"
			:filters="filters"
			:list-state="list"
			@update:page="page = $event"
			@update:limit="limit = $event"
			@update:search="search = $event"
			@update:sort="sort = $event"
			@update:order="order = $event"
			@update:filters="filters = $event"
			@row-click="open"
		>
			<template #cell-taskId="{ row }">
				<span class="font-medium">{{ taskLabel(row) }}</span>
			</template>
			<template #cell-assigneeName="{ row, value }">
				<span v-if="value != null" class="inline-flex items-center gap-2">
					<MemberAvatar
						:member="{
							name: String(value),
							avatarUrl: typeof row.assigneeAvatarUrl === 'string' ? row.assigneeAvatarUrl : null,
						}"
						class="size-6"
						fallback-class="text-xs"
					/>
					<span>{{ value }}</span>
				</span>
				<!-- An OS-origin run belongs to the console itself: the kit pill's system tone is that channel. -->
				<ZhStatusPill v-else-if="row.taskOrigin === 'os'" data-assignee="os" tone="system">OS</ZhStatusPill>
				<span v-else class="text-muted-foreground">Unassigned</span>
			</template>
			<template #cell-status="{ row }">
				<span class="inline-flex items-center gap-2">
					<!--
						BG §18.4: which state a status is gets decided once, in @/lib/statusTone; what that state looks
						like (colour, glyph, the live blink on a running run) is the kit pill's call.
					-->
					<!-- A run that dead-ended is stored `succeeded` but stopped its pipeline: it reads stopped, and says why. -->
					<ZhTooltip :text="runStoppedLine(row as unknown as RunRow)">
						<ZhStatusPill
							data-testid="run-status-chip"
							:data-waiting="waiting.has(String(row.status)) ? 'true' : 'false'"
							:data-tone="statusTone(runPillStatus(row as unknown as RunRow))"
							:data-stopped-reason="runStoppedLine(row as unknown as RunRow) ?? undefined"
							:tone="statusTone(runPillStatus(row as unknown as RunRow))"
						>{{ runPillStatus(row as unknown as RunRow) }}</ZhStatusPill>
					</ZhTooltip>
					<Button
						v-if="retryable.has(String(row.status))"
						type="button"
						size="sm"
						variant="outline"
						:disabled="retrying === String(row.id)"
						@click.stop="retry(String(row.id))"
					>
						Retry
					</Button>
				</span>
			</template>
			<template #cell-createdAt="{ row }">
				<ZhTooltip :text="startedTitle(row)"><span class="text-muted-foreground tabular-nums">{{ startedAgo(row) }}</span></ZhTooltip>
			</template>
			<template #cell-duration="{ row }">
				<span class="tabular-nums">{{ duration(row) }}</span>
			</template>
			<template #cell-skipReason="{ row }">
				<span v-if="skipReason(row as unknown as RunRow)" class="text-sm">
					{{ skipReason(row as unknown as RunRow) }}
				</span>
				<span v-else class="text-muted-foreground">—</span>
			</template>
			<template #cell-id="{ value }">
				<span class="font-mono text-xs">{{ String(value).slice(0, 8) }}</span>
			</template>
		</DataTable>
	</section>
</template>
